"use client"

import Image from "next/image"
import Link from "next/link"
import { ROUTES } from "@/config/routes"
import { cn } from "@/lib/utils"
import { HomeSection } from "./HomeSection"
import { StaggerContainer, StaggerItem } from "@/components/animations"

type RefactionCardType = {
  title: string
  description?: string
  image: string
  url: string
  isExternalLink?: boolean
  buttonText?: string
  imageContainerClass?: string
}

const refactions: RefactionCardType[] = [
  {
    title: "Compra refacciones desde cualquier lugar, en cualquier momento.",
    image: "/images/home/pcc.webp",
    url: "https://parts.cat.com/",
    isExternalLink: true,
    buttonText: "PARTS.CAT.COM",
    imageContainerClass: "bg-[#E3E3E3]",
  },
  {
    title: "Obtén el óptimo funcionamiento de tus equipos con refacciones originales Cat.",
    image: "/images/home/partes-originales-cat.webp",
    url: ROUTES.partsServices.details("refacciones"),
  },
  {
    title: "Más resistencia y más tracción con llantas Maxam.",
    image: "/images/home/llantas.webp",
    url: ROUTES.tires.root,
  },
]

const RefactionCard = ({ refaction }: { refaction: RefactionCardType }) => (
  <Link
    href={refaction.url}
    {...(refaction.isExternalLink ? { target: "_blank" } : {})}
    className="block h-full w-full group"
  >
    {/* Mobile version */}
    <div className="flex bg-white rounded-lg overflow-hidden p-3 min-h-[120px] sm:hidden transition-all duration-300  hover:scale-[1.02]">
      <div className={cn("relative w-[90px] h-[90px] flex-shrink-0 transition-transform duration-300 group-hover:scale-105", refaction.imageContainerClass)}>
        <Image
          fill
          className="object-cover rounded-md"
          src={refaction.image}
          alt={refaction.title.toLowerCase()}
        />
      </div>
      <div className="flex-1 flex flex-col justify-center pl-4">
        <div>
          <h3 className="text-lg font-bold uppercase inline">{refaction.title}</h3>
          {refaction.description && (
            <p className="text-sm text-black/80 mb-1 inline ml-1">{refaction.description}</p>
          )}
        </div>
        {refaction.buttonText && refaction.isExternalLink && (
          <span className="mt-1 inline-block px-3 py-1 text-xs bg-matco rounded-md w-fit transition-all duration-300 group-hover:shadow-md">
            {refaction.buttonText}
          </span>
        )}
      </div>
    </div>

    {/* Desktop version */}
    <div className="hidden sm:flex flex-col rounded-lg overflow-hidden bg-white cursor-pointer h-full transition-all duration-500 hover:scale-[1.03] hover:-translate-y-2">
      <div className={cn("relative w-full flex-shrink-0 overflow-hidden", refaction.imageContainerClass)} style={{ aspectRatio: '366/345' }}>
        <Image
          fill
          className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
          src={refaction.image}
          alt={refaction.title.toLowerCase()}
        />
      </div>
      <div className="flex flex-col justify-start items-center text-center py-6 px-8 space-y-2 flex-grow">
        <div>
          <h3 className="text-lg lg:text-xl font-bold uppercase inline">{refaction.title}</h3>
          {refaction.description && (
            <p className="text-sm lg:text-base inline ml-1">{refaction.description}</p>
          )}
        </div>
        {refaction.buttonText && refaction.isExternalLink && (
          <span className="mt-2 inline-block px-4 py-2 text-sm bg-matco rounded-md w-fit transition-all duration-300 group-hover:shadow-lg">
            {refaction.buttonText}
          </span>
        )}
      </div>
    </div>
  </Link>
)

export const Refaction = () => {
  return (
    <HomeSection
      title="REFACCIONES"
      subtitle="ENCUENTRA LAS REFACCIONES QUE NECESITAS PARA TU MAQUINARIA."
      viewAllLink={ROUTES.partsServices.details("/refacciones")}
      className="bg-light01"
    >
      <StaggerContainer staggerDelay={0.15} className="flex flex-col sm:grid sm:grid-cols-3 gap-4">
        {refactions.map((refaction, index) => (
          <StaggerItem key={index}>
            <RefactionCard refaction={refaction} />
          </StaggerItem>
        ))}
      </StaggerContainer>
    </HomeSection>
  )
}